import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  ArrowLeft, Check, Users, Lightbulb, Wrench, Target, 
  MessageCircleQuestion, Clock, AlertTriangle, Zap, ArrowRight
} from "lucide-react";
import { Link } from "react-router-dom";

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-lg border-b border-[#C9A227]/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 border-2 border-[#C9A227] rounded-lg flex items-center justify-center">
                <span className="text-[#C9A227] font-bold text-sm">NL</span>
              </div>
              <span className="font-semibold text-gray-900">Next Level Protocol</span>
            </Link>
            
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="outline" className="gap-2 border-2 border-[#C9A227] text-[#C9A227] hover:bg-[#C9A227] hover:text-white">
                  <ArrowLeft className="w-4 h-4" /> Terug naar Home
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Badge className="badge-accent mb-6">Over Ons</Badge>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 tracking-tight mb-6">
            Wie zijn wij
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            Gebouwd door vakmensen, voor vakmensen. Onze app is ontstaan vanuit de dagelijkse praktijk 
            van de vloerenbranche.
          </p>
        </div>
      </section>

      {/* Maurice Introduction */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-8 items-start">
            <div className="w-24 h-24 bg-[#C9A227]/10 border-2 border-[#C9A227] rounded-2xl flex items-center justify-center flex-shrink-0">
              <Users className="w-12 h-12 text-[#C9A227]" />
            </div>
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
                Maurice Abels
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                Mijn naam is Maurice Abels en sinds mijn 18e ben ik actief in de vloerenbranche. 
                Wat begon met houten vloeren, groeide al snel uit tot jarenlange ervaring in 
                gietvloeren en PVC vloeren.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Met een bedrijf dat jaarlijks zo'n <span className="font-semibold text-gray-900">600 particuliere projecten</span> uitvoerde, 
                kwamen we dagelijks in aanraking met alles wat erbij komt kijken om een vloerproject 
                soepel te laten verlopen.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-16 md:py-24 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-6">
            <MessageCircleQuestion className="w-8 h-8 text-[#C9A227]" />
            <h2 className="text-2xl md:text-3xl font-bold text-white">
              Een herkenbaar probleem in de praktijk
            </h2>
          </div>
          
          <p className="text-lg text-gray-300 leading-relaxed mb-8">
            Door de jaren heen liepen we steeds tegen dezelfde uitdaging aan: 
            <span className="text-white font-semibold"> het goed overbrengen van richtlijnen en 
            voorbereidingsvoorwaarden aan klanten</span>.
          </p>

          <p className="text-lg text-gray-300 leading-relaxed mb-8">
            Voor een vloerenlegger zijn veel zaken vanzelfsprekend. Maar voor een particuliere klant 
            – vaak niet technisch – is het soms lastig te begrijpen wat er precies nodig is voordat 
            een vloer gelegd kan worden.
          </p>

          <div className="bg-gray-800/50 border-2 border-[#C9A227]/30 rounded-2xl p-8 mb-8">
            <h3 className="text-xl font-semibold mb-6 text-[#C9A227]">Het gevolg?</h3>
            <div className="space-y-4">
              {[
                { icon: MessageCircleQuestion, text: "Veel vragen over checklists en voorbereiding" },
                { icon: Clock, text: "Extra telefoontjes en berichten" },
                { icon: AlertTriangle, text: "Misverstanden ondanks duidelijke documenten" },
                { icon: Wrench, text: "Werkbezoeken die niet voldoende bleken" },
                { icon: AlertTriangle, text: "Soms zelfs het moeten afbreken of verplaatsen van een klus" }
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-4">
                  <item.icon className="w-5 h-5 text-red-400 flex-shrink-0" />
                  <span className="text-gray-300">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          <p className="text-lg text-gray-300 leading-relaxed">
            Dat zorgde voor frustratie bij klanten én vakmensen. En vooral: 
            <span className="text-white font-semibold"> het kostte enorm veel tijd en energie</span>.
          </p>
        </div>
      </section>

      {/* Innovation Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-6">
            <Lightbulb className="w-8 h-8 text-[#C9A227]" />
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
              Van frustratie naar innovatie
            </h2>
          </div>

          <p className="text-lg text-gray-600 leading-relaxed mb-8">
            In plaats van ons neer te leggen bij deze inefficiëntie, besloten we het anders aan te pakken.
          </p>

          <div className="bg-[#C9A227]/5 border-2 border-[#C9A227] rounded-2xl p-8 mb-8">
            <p className="text-lg text-gray-700 leading-relaxed">
              Vanuit onze dagelijkse praktijk hebben wij een slimme oplossing ontwikkeld: 
              <span className="font-semibold text-[#C9A227]"> een checklist- en protocolapp speciaal 
              voor de vloerenbranche</span>.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed mt-4">
              Een tool waarmee vakbedrijven hun richtlijnen veel duidelijker kunnen communiceren 
              en projecten beter kunnen voorbereiden.
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-6">
            <Zap className="w-8 h-8 text-[#C9A227]" />
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
              Wat onze app mogelijk maakt
            </h2>
          </div>

          <p className="text-lg text-gray-600 leading-relaxed mb-8">
            Met onze app kunnen bedrijven:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              "Klanten stap voor stap begeleiden",
              "Duidelijke digitale checklists delen",
              "Protocollen centraal beheren",
              "Minder misverstanden en faalkosten ervaren",
              "Efficiënter werken en tijd besparen",
              "Professioneler communiceren"
            ].map((feature, index) => (
              <div key={index} className="flex items-center gap-3 bg-white p-4 rounded-xl border-2 border-gray-200 hover:border-[#C9A227] transition-colors">
                <div className="w-8 h-8 bg-[#C9A227]/10 border border-[#C9A227] rounded-full flex items-center justify-center flex-shrink-0">
                  <Check className="w-5 h-5 text-[#C9A227]" />
                </div>
                <span className="text-gray-700 font-medium">{feature}</span>
              </div>
            ))}
          </div>

          <p className="text-lg text-gray-600 leading-relaxed mt-8">
            Wat ooit begon als een interne oplossing, is nu uitgegroeid tot een product dat wij 
            aanbieden aan andere vloerenbedrijven die dezelfde uitdagingen ervaren.
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 md:py-24 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <Target className="w-8 h-8 text-[#C9A227]" />
            <h2 className="text-2xl md:text-3xl font-bold text-white">
              Gebouwd door vakmensen, voor vakmensen
            </h2>
          </div>

          <p className="text-lg text-gray-300 leading-relaxed mb-8 max-w-2xl mx-auto">
            Onze kracht is dat we deze app niet hebben bedacht vanuit een kantoor, maar 
            <span className="text-white font-semibold"> vanuit de werkvloer</span>. 
            Wij weten precies waar het in de praktijk misgaat – en hoe je dat voorkomt.
          </p>

          <div className="bg-gray-800 border-2 border-[#C9A227] rounded-2xl p-8 max-w-2xl mx-auto">
            <p className="text-xl text-white font-semibold mb-2">
              Met deze tool helpen we de branche vooruit:
            </p>
            <p className="text-lg text-[#C9A227]">
              minder frustratie, meer duidelijkheid en betere projecten.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
            Klaar om uw werkprocessen te verbeteren?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Ontdek hoe de Next Level Protocol App ook uw bedrijf kan helpen.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/#pricing">
              <Button className="bg-[#C9A227] border-2 border-[#C9A227] text-white hover:bg-[#B8922A] hover:border-[#B8922A] px-8 py-6 text-lg font-semibold">
                Bekijk Pakketten <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/#demo">
              <Button variant="outline" className="px-8 py-6 text-lg border-2 border-[#C9A227] text-[#C9A227] hover:bg-[#C9A227] hover:text-white">
                Bekijk Demo
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-gray-900 border-t border-[#C9A227]/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 border-2 border-[#C9A227] rounded-lg flex items-center justify-center">
                <span className="text-[#C9A227] font-bold text-sm">NL</span>
              </div>
              <span className="font-semibold text-white">Next Level Protocol</span>
            </Link>
            <div className="flex items-center gap-6 text-sm text-gray-400">
              <a href="mailto:nextlevelgietvloeren@gmail.com" className="hover:text-[#C9A227] transition-colors">
                nextlevelgietvloeren@gmail.com
              </a>
            </div>
            <div className="text-sm text-gray-500">
              © 2024 Next Level Protocol. Alle rechten voorbehouden.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
